<script setup>
import { computed } from 'vue'
import navItems from '@/navigation/vertical'
import { themeConfig } from '@themeConfig'
import { useSidebarBadges, useSidebarBadgesPoller } from '@/composables/useSidebarBadges'

// Components
import Footer from '@/layouts/components/Footer.vue'
import NavBarNotifications from '@/layouts/components/NavBarNotifications.vue'
import NavBarAutoMatchBell from '@/layouts/components/NavBarAutoMatchBell.vue'
import NavSearchBar from '@/layouts/components/NavSearchBar.vue'
import UserProfile from '@/layouts/components/UserProfile.vue'
import NavBarI18n from '@core/components/I18n.vue'
import NavbarThemeSwitcher from '@/layouts/components/NavbarThemeSwitcher.vue'

// @layouts plugin
import { VerticalNavLayout } from '@layouts'

// Live sidebar badges. Polls /iam claims + pending PL rows; an axios
// interceptor also auto-refreshes after relevant mutations so the
// badges drop to 0 the moment an operator clears a claim, instead of
// waiting for the next 60s poll tick.
useSidebarBadgesPoller()

const { claimsCount, importsCount, unclaimedCount, inboxTotal } = useSidebarBadges()

const dynamicBadgeValues = computed(() => ({
  chatIdentityClaims: claimsCount.value,
  pendingImports: importsCount.value,
  unclaimedParcels: unclaimedCount.value,
  inboxTotal: inboxTotal.value,
}))

// Walk the static nav tree, swap the `_dynamicBadge` sentinel for a
// real badgeContent string. Always renders the badge — count=0 shows
// as a grey "0" so the seller knows polling is alive even when no
// claims are pending. Count>0 turns red.
function injectBadges(items) {
  const values = dynamicBadgeValues.value
  
  return items.map(item => {
    const next = { ...item }
    if (item._dynamicBadge) {
      const v = values[item._dynamicBadge] || 0

      next.badgeContent = String(v)
      next.badgeClass = v > 0 ? 'bg-error' : 'bg-secondary'
    }
    if (Array.isArray(item.children)) {
      next.children = injectBadges(item.children)
    }
    
    return next
  })
}

const liveNavItems = computed(() => injectBadges(navItems))
</script>

<template>
  <VerticalNavLayout :nav-items="liveNavItems">
    <!-- 👉 navbar -->
    <template #navbar="{ toggleVerticalOverlayNavActive }">
      <div class="d-flex h-100 align-center">
        <IconBtn
          id="vertical-nav-toggle-btn"
          class="ms-n3 d-lg-none"
          @click="toggleVerticalOverlayNavActive(true)"
        >
          <VIcon
            size="26"
            icon="tabler-menu-2"
          />
        </IconBtn>

        <NavSearchBar class="ms-lg-n3" />

        <VSpacer />

        <NavBarI18n
          v-if="themeConfig.app.i18n.enable && themeConfig.app.i18n.langConfig?.length"
          :languages="themeConfig.app.i18n.langConfig"
        />
        <NavbarThemeSwitcher class="me-1" />
        <NavBarAutoMatchBell class="me-1" />
        <NavBarNotifications class="me-1" />
        <UserProfile />
      </div>
    </template>

    <!-- 👉 Pages -->
    <slot />

    <!-- 👉 Footer -->
    <template #footer>
      <Footer />
    </template>
  </VerticalNavLayout>
</template>
